import { Button, Spin, Tag, message as antdMessage } from "antd";
import {
  ArrowLeftOutlined,
  InboxOutlined,
  ReloadOutlined,
  TagsOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AttachmentChips } from "../components/AttachmentChips";
import { HtmlBody } from "../components/HtmlBody";
import { CaseTagsEditor } from "../components/CaseTagsEditor";
import { useWs } from "../hooks/useWs";
import { getCase, resummarizeCase, setCaseStatus } from "../api/cases";
import type { CaseDetail, CaseMessage } from "../api/cases";
import { formatDateTime } from "../utils/time";

export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [resumming, setResumming] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const load = async (caseId: string) => {
    try {
      const { case: c } = await getCase(caseId);
      setData(c);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useWs((ev) => {
    if ((ev.type === "case:updated" || ev.type === "case:new") && ev.caseId === id) {
      void (id && load(id));
    }
  });

  const onResummarize = async () => {
    if (!id) return;
    setResumming(true);
    try {
      await resummarizeCase(id);
      antdMessage.success("已重新触发 AI 分析，稍后自动刷新");
    } catch {
      antdMessage.error("操作失败");
    } finally {
      setResumming(false);
    }
  };

  const onToggleArchive = async () => {
    if (!id || !data) return;
    const next = data.status === "closed" ? "open" : "closed";
    setArchiving(true);
    try {
      await setCaseStatus(id, next);
      setData({ ...data, status: next });
      antdMessage.success(next === "closed" ? "已归档" : "已取消归档");
    } catch {
      antdMessage.error("操作失败");
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center" }}>
        <Spin />
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 32 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>案件不存在</div>
          <Button onClick={() => navigate("/cases")}>返回案件列表</Button>
        </div>
      </div>
    );
  }

  const message = data.message;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 18px",
          borderBottom: "1px solid var(--ant-color-border)",
          background: "var(--ant-color-bg-container)",
        }}
      >
        <Button
          type="text"
          shape="circle"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/cases")}
          aria-label="返回"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ant-color-primary)", letterSpacing: 0.3 }}>
            {data.caseId}
            {data.status === "closed" ? (
              <Tag style={{ marginLeft: 8 }}>已归档</Tag>
            ) : null}
            {data.gmailLabelId ? (
              <Tag style={{ marginLeft: 8 }} color="processing">
                已同步 Gmail
              </Tag>
            ) : null}
          </div>
          <h2
            style={{
              margin: "2px 0 0",
              fontSize: 19,
              fontWeight: 700,
              letterSpacing: -0.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.title || "(未命名案件)"}
          </h2>
        </div>
        <Button icon={<ReloadOutlined />} loading={resumming} onClick={onResummarize}>
          重新分析
        </Button>
        <Button
          icon={data.status === "closed" ? <UndoOutlined /> : <InboxOutlined />}
          loading={archiving}
          onClick={onToggleArchive}
        >
          {data.status === "closed" ? "取消归档" : "归档"}
        </Button>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 18 }}>
        <div style={{ maxWidth: 960 }}>
        <section
          style={{
            border: "1px solid var(--ant-color-border)",
            borderRadius: 14,
            padding: 16,
            background: "var(--ant-color-bg-container)",
            marginBottom: 18,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ant-color-text-secondary)", marginBottom: 8 }}>
            AI 摘要
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {data.summary || "AI 正在生成摘要…"}
          </div>
          {data.keywords.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 14 }}>
              <TagsOutlined style={{ color: "var(--ant-color-text-secondary)" }} />
              {data.keywords.map((k) => (
                <Tag key={k} style={{ marginRight: 0 }}>
                  {k}
                </Tag>
              ))}
            </div>
          ) : null}
        </section>

        <section
          style={{
            border: "1px solid var(--ant-color-border)",
            borderRadius: 14,
            padding: 16,
            background: "var(--ant-color-bg-container)",
            marginBottom: 18,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ant-color-text-secondary)", marginBottom: 10 }}>
            标签
          </div>
          <CaseTagsEditor
            caseId={data.id}
            value={data.tags}
            onChange={(tags) => setData({ ...data, tags })}
          />
        </section>

        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ant-color-text-secondary)", marginBottom: 10 }}>
          原始邮件
        </div>
        {message ? (
          <CaseMessageCard m={message} />
        ) : (
          <div style={{ color: "var(--ant-color-text-secondary)", fontSize: 14 }}>
            原始邮件已不可用
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function CaseMessageCard({ m }: { m: CaseMessage }) {
  return (
    <div
      style={{
        border: "1px solid var(--ant-color-border)",
        borderRadius: 12,
        background: "var(--ant-color-bg-container)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          padding: "10px 14px",
          borderBottom: "1px solid var(--ant-color-border)",
          fontSize: 14,
          color: "var(--ant-color-text-secondary)",
        }}
      >
        <span style={{ fontWeight: 500, color: "var(--ant-color-text)" }}>{m.fromEmail}</span>
        <span>{formatDateTime(m.receivedAt)}</span>
      </div>
      <div
        style={{
          padding: m.bodyHtml ? "8px 10px" : "12px 14px",
          fontSize: 16,
          lineHeight: 1.6,
          wordBreak: "break-word",
          ...(m.bodyHtml ? {} : { whiteSpace: "pre-wrap" as const }),
        }}
      >
        {m.bodyHtml ? (
          <HtmlBody html={m.bodyHtml} messageId={m.id} attachments={m.attachments} />
        ) : (
          m.body || m.snippet || "(空)"
        )}
        <AttachmentChips
          messageId={m.id}
          attachments={m.attachments}
          showInline={!(m.bodyHtml && /cid:/i.test(m.bodyHtml))}
        />
      </div>
    </div>
  );
}
